import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/account/auth.service';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService, private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip authentication for anonymous requests
    if (this.isAnonymousRequest(req)) {
      return next.handle(req);
    }

    const accountJson = localStorage.getItem('account');
    if (!accountJson) {
      return next.handle(req); // Skip if no account data is found
    }

    const account = JSON.parse(accountJson);
    const { token, refreshToken, userId } = account;
    const expiration = new Date(account.expiration).getTime();
    const currentTime = new Date().getTime();

    const roles = this.getRoles(token); // Extract user roles from token
    const expectedRoles = this.getRouteRoles(); // Get required roles from route metadata

    // Redirect if the user lacks required roles
    if (expectedRoles.length && !this.hasRequiredRole(roles, expectedRoles)) {
      this.redirectToLogin(expectedRoles);
      return throwError('Unauthorized access');
    }

    // If token is expired, refresh it before proceeding
    if (currentTime >= expiration) {
      return this.authService.getJWTbyRefreshToken({ refreshToken, userId }).pipe(
        switchMap(response => {
          if (response) {
            const newRoles = this.getRoles(response.token);
            if (expectedRoles.length && !this.hasRequiredRole(newRoles, expectedRoles)) {
              this.redirectToLogin(expectedRoles);
              return throwError('Unauthorized access');
            }

            // Update local storage with new token
            const updatedAccount = { ...account, token: response.token, expiration: response.expiration };
            localStorage.setItem('account', JSON.stringify(updatedAccount));

            // Clone the request with the new token and continue
            const clonedRequest = this.addTokenToRequest(req, response.token);
            return next.handle(clonedRequest);
          } else {
            this.redirectToLogin(expectedRoles);
            return throwError('Failed to refresh token');
          }
        }),
        catchError(err => {
          localStorage.removeItem('account');
          this.redirectToLogin(expectedRoles);
          return throwError(err);
        })
      );
    }

    // Token is valid, attach it to the request and proceed
    const clonedRequest = this.addTokenToRequest(req, token);
    return next.handle(clonedRequest);
  }

  /**
   * Redirects user to appropriate login page based on expected roles
   */
  private redirectToLogin(expectedRoles: string[]) {
    const roleRedirects: Record<string, string> = {
      SuperAdmin: '/partner-signin',
      Admin: '/partner-signin',
      Mentor: '/partner-signin',
      PeerReviewer: '/partner-signin',
      SchoolAdmin: '/partner-signin',
      Operations: '/partner-signin',
      Student: '/signin',
    };
    const path =
      expectedRoles.map((r) => roleRedirects[r]).find(Boolean) || '/signin';
    this.router.navigate([path]);
  }

  /**
   * Adds JWT token to the request headers
   */
  private addTokenToRequest(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * Determines if a request is anonymous (does not require authentication)
   */
  private isAnonymousRequest(req: HttpRequest<any>): boolean {
    const path = req.url.split('?')[0];

    // The login endpoint is anonymous, but 'api/user/login-streak' (and any other
    // 'login'-prefixed path) requires auth — match the login endpoint exactly so a
    // substring check doesn't accidentally strip the token from those requests.
    if (path.endsWith('api/user/login')) {
      return true;
    }

    const anonymousUrls = [
      'api/user/register', 'api/user/verify', 'api/user/jwttoken',
      'www.googleapis.com', 'youtube.com', 'blog.gradpilots.com', 'api/user/google-login',
      'api/operations/signup', 'api/institutes/search', 'api/v2/institutes/institute/courses',
      'api/roi-calculator/calculate'
    ];
    return anonymousUrls.some(url => path.includes(url));
  }

  /**
   * Retrieves expected roles for the current route
   */
  private getRouteRoles(): string[] {
    let last: string[] = [];
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      const er = route.data?.['expectedRoles'];
      if (Array.isArray(er) && er.length) {
        last = er;
      }
      route = route.firstChild ?? null;
    }
    return last;
  }

  /**
   * Checks if user has at least one of the required roles
   */
  private hasRequiredRole(userRoles: string[], expectedRoles: string[]): boolean {
    return expectedRoles.some(role => userRoles.includes(role));
  }

  /**
   * Extracts user roles from the JWT token
   */
  private getRoles(token: string): string[] {
    if (!token) return [];
    try {
      const decodedToken: { [key: string]: unknown } = jwtDecode(token);
      const raw = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] as string | string[] | undefined;
      if (raw == null) return [];
      return Array.isArray(raw) ? raw : [raw];
    } catch (error) {
      console.error('Invalid token', error);
      return [];
    }
  }
}
